import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { UserRoleListItem } from '@/services/userRoleService';
import type { RoleUser } from '../tabs/UsersTab';

export interface TransferUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: RoleUser | null;
  currentRoleName: string;
  availableRoles: UserRoleListItem[];
  newRoleId: string;
  onNewRoleIdChange: (roleId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export const TransferUserDialog: React.FC<TransferUserDialogProps> = ({
  open,
  onOpenChange,
  user,
  currentRoleName,
  availableRoles,
  newRoleId,
  onNewRoleIdChange,
  onConfirm,
  onCancel,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Transfer User to Different Role</DialogTitle>
        <DialogDescription>Select a new role for {user?.name}. This will immediately update their permissions.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>Current Role</Label>
          <div className="p-3 rounded-lg border bg-muted/50"><p className="font-medium">{currentRoleName}</p></div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-role">New Role</Label>
          <Select value={newRoleId} onValueChange={onNewRoleIdChange}>
            <SelectTrigger id="new-role"><SelectValue placeholder="Select a role" /></SelectTrigger>
            <SelectContent>
              {availableRoles.map(role => (
                <SelectItem key={role.id} value={String(role.id)}>
                  <div className="flex items-center gap-2">
                    <span>{role.name}</span>
                    {role.isSystem && <Badge variant="secondary" className="text-xs">Platform</Badge>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {user && (
          <div className="p-3 rounded-lg border bg-info-50 dark:bg-info-950/20">
            <p className="text-sm text-muted-foreground">
              <strong>{user.name}</strong> will be transferred from <strong>{currentRoleName}</strong> to the selected role.
            </p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} disabled={!newRoleId}>Transfer User</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
